import { useCallback, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { ANALYTICS_CHANNEL } from '../analytics';

const WINDOW_MS = 10_000;
// Generous so genuine prop changes never trip it; only a runaway re-render loop
// (PIRA-7455 / PIR-301897) reaches this many dispatches per window.
const ANOMALY_THRESHOLD = 30;
// One signal per runaway episode, rather than a flood of anomaly events.
const REPORT_COOLDOWN_MS = 60_000;

/**
 * Reports an operational analytics event when `propsUpdated` messages are
 * dispatched to the embedded Confluence iframe at an anomalously high rate,
 * to detect third-party consumers whose integration re-renders excessively.
 */
export const usePropsUpdatedFrequencyMonitor = (
	uniqueKey: string,
): { recordPropsUpdated: () => void } => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const timestampsRef = useRef<number[]>([]);
	const lastReportedAtRef = useRef<number>(0);

	const recordPropsUpdated = useCallback(() => {
		const now = Date.now();
		const timestamps = timestampsRef.current;

		timestamps.push(now);

		const windowStart = now - WINDOW_MS;
		while (timestamps.length > 0 && timestamps[0] < windowStart) {
			timestamps.shift();
		}

		if (timestamps.length < ANOMALY_THRESHOLD) {
			return;
		}

		if (now - lastReportedAtRef.current < REPORT_COOLDOWN_MS) {
			return;
		}
		lastReportedAtRef.current = now;

		const count = timestamps.length;
		createAnalyticsEvent({
			action: 'detected',
			actionSubject: 'embeddedConfluencePropsUpdatedAnomaly',
			eventType: 'operational',
			attributes: {
				count,
				windowMs: WINDOW_MS,
				ratePerMinute: Math.round((count / WINDOW_MS) * 60_000),
				threshold: ANOMALY_THRESHOLD,
				uniqueKey,
			},
		}).fire(ANALYTICS_CHANNEL);
	}, [createAnalyticsEvent, uniqueKey]);

	return { recordPropsUpdated };
};
